import CSS from 'csstype';

import { publicaColors } from '~/src/dash/utils/colors';

export interface ILayoutProps {
  style?: CSS.Properties;
  // responsive props
  // you must add this prop if you want to use the styles below
  // this is to add another layer to ensure you want to use custom hooks
  // responsive will activate the custom dimension hooks
  // use this only when necessary
  responsive?: boolean;
  // you must add the responsive prop above to activate the styles below
  // this styling will be added when the browser is extraLarge or smaller
  extraLargeStyle?: CSS.Properties;
  // this styling will be added when the browser is large or smaller
  largeStyle?: CSS.Properties;
  // this styling will be added when the browser is medium or smaller
  mediumStyle?: CSS.Properties;
  // this styling will be added when the browser is small or smaller
  smallStyle?: CSS.Properties;
  // this styling will be added when the browser is extraSmall or smaller
  extraSmallStyle?: CSS.Properties;
  // basic
  children?: any;
  innerRef?: any;
  className?: string;
  // use this to specifically mark a div for easier look up in a browser inspect tool
  // use only for local/staging. do not include into PR
  tag?: string;
  cypressID?: string;
}

export interface ISpacingProps {
  // uniform padding
  noPadding?: boolean;
  extraSmallPadding?: boolean;
  smallPadding?: boolean;
  basePadding?: boolean;
  largePadding?: boolean;
  extraLargePadding?: boolean;
  hugePadding?: boolean;
  // specific padding
  paddingTop?: string;
  paddingRight?: string;
  paddingBottom?: string;
  paddingLeft?: string;
  // preset padding
  noHorizontalPadding?: boolean;
  extraSmallHorizontalPadding?: boolean;
  smallHorizontalPadding?: boolean;
  horizontalPadding?: boolean;
  largeHorizontalPadding?: boolean;
  extraLargeHorizontalPadding?: boolean;
  hugeHorizontalPadding?: boolean;
  noVerticalPadding?: boolean;
  extraSmallVerticalPadding?: boolean;
  smallVerticalPadding?: boolean;
  verticalPadding?: boolean;
  largeVerticalPadding?: boolean;
  extraLargeVerticalPadding?: boolean;
  hugeVerticalPadding?: boolean;
  // preset padding v2 rem
  extraSmallPaddingV2?: boolean;
  smallPaddingV2?: boolean;
  basePaddingV2?: boolean;
  largePaddingV2?: boolean;
  extraLargePaddingV2?: boolean;
  hugePaddingV2?: boolean;
  noHorizontalPaddingV2?: boolean;
  extraSmallHorizontalPaddingV2?: boolean;
  smallHorizontalPaddingV2?: boolean;
  horizontalPaddingV2?: boolean;
  largeHorizontalPaddingV2?: boolean;
  extraLargeHorizontalPaddingV2?: boolean;
  hugeHorizontalPaddingV2?: boolean;
  noVerticalPaddingV2?: boolean;
  extraSmallVerticalPaddingV2?: boolean;
  smallVerticalPaddingV2?: boolean;
  verticalPaddingV2?: boolean;
  largeVerticalPaddingV2?: boolean;
  extraLargeVerticalPaddingV2?: boolean;
  hugeVerticalPaddingV2?: boolean;
  // specific margins
  marginTop?: string;
  marginRight?: string;
  marginBottom?: string;
  marginLeft?: string;
}

export type panelType = 'base' | 'muted' | 'highlighted' | 'highlightedV2' | 'paper';

export const panelTypes = {
  BASE: 'base',
  MUTED: 'muted',
  HIGHLIGHTED: 'highlighted',
  HIGHLIGHTEDV2: 'highlightedV2',
  PAPER: 'paper',
} as Record<string, panelType>;

export type spacingSize =
  | 'none'
  | 'extraSmall'
  | 'small'
  | 'medium'
  | 'large'
  | 'extraLarge'
  | 'huge'
  | 'base';

export const spacing = {
  none: '0px',
  extraSmall: '4px',
  small: '8px',
  medium: '16px',
  large: '24px',
  extraLarge: '32px',
  huge: '64px',
  // use base only when you want a default
  base: '16px',
} as Record<spacingSize, string>;

export const spacingV2 = {
  none: '0rem',
  extraSmall: '0.25rem',
  small: '0.5rem',
  medium: '1rem',
  large: '1.5rem',
  extraLarge: '2rem',
  huge: '4rem',
  // use base only when you want a default
  base: '1rem',
} as Record<spacingSize, string>;

interface IGetSpacingStyleParams extends ISpacingProps {
  defaultPadding: string;
}

export const getSpacingStyle = ({
  defaultPadding,
  noPadding,
  extraSmallPadding,
  smallPadding,
  basePadding,
  largePadding,
  extraLargePadding,
  hugePadding,
  noHorizontalPadding,
  extraSmallHorizontalPadding,
  smallHorizontalPadding,
  horizontalPadding,
  largeHorizontalPadding,
  extraLargeHorizontalPadding,
  hugeHorizontalPadding,
  noVerticalPadding,
  extraSmallVerticalPadding,
  smallVerticalPadding,
  verticalPadding,
  largeVerticalPadding,
  extraLargeVerticalPadding,
  hugeVerticalPadding,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,
  marginTop,
  marginRight,
  marginBottom,
  marginLeft,
  extraSmallPaddingV2,
  smallPaddingV2,
  basePaddingV2,
  largePaddingV2,
  extraLargePaddingV2,
  hugePaddingV2,
  noHorizontalPaddingV2,
  extraSmallHorizontalPaddingV2,
  smallHorizontalPaddingV2,
  horizontalPaddingV2,
  largeHorizontalPaddingV2,
  extraLargeHorizontalPaddingV2,
  hugeHorizontalPaddingV2,
  noVerticalPaddingV2,
  extraSmallVerticalPaddingV2,
  smallVerticalPaddingV2,
  verticalPaddingV2,
  largeVerticalPaddingV2,
  extraLargeVerticalPaddingV2,
  hugeVerticalPaddingV2,
}: IGetSpacingStyleParams) => {
  const styles = { padding: defaultPadding } as CSS.Properties;
  const { none, extraSmall, small, base, large, extraLarge, huge } = spacing;

  if (noPadding) {
    styles.padding = none;
  }
  if (extraSmallPadding) {
    styles.padding = extraSmall;
  }
  if (smallPadding) {
    styles.padding = small;
  }
  if (basePadding) {
    styles.padding = base;
  }
  if (largePadding) {
    styles.padding = large;
  }
  if (extraLargePadding) {
    styles.padding = extraLarge;
  }
  if (hugePadding) {
    styles.padding = huge;
  }
  if (noHorizontalPadding) {
    styles.paddingRight = none;
    styles.paddingLeft = none;
  }
  if (extraSmallHorizontalPadding) {
    styles.paddingRight = extraSmall;
    styles.paddingLeft = extraSmall;
  }
  if (smallHorizontalPadding) {
    styles.paddingRight = small;
    styles.paddingLeft = small;
  }
  if (horizontalPadding) {
    styles.paddingRight = base;
    styles.paddingLeft = base;
  }
  if (largeHorizontalPadding) {
    styles.paddingRight = large;
    styles.paddingLeft = large;
  }
  if (extraLargeHorizontalPadding) {
    styles.paddingRight = extraLarge;
    styles.paddingLeft = extraLarge;
  }
  if (hugeHorizontalPadding) {
    styles.paddingRight = huge;
    styles.paddingLeft = huge;
  }
  if (noVerticalPadding) {
    styles.paddingTop = none;
    styles.paddingBottom = none;
  }
  if (extraSmallVerticalPadding) {
    styles.paddingTop = extraSmall;
    styles.paddingBottom = extraSmall;
  }
  if (smallVerticalPadding) {
    styles.paddingTop = small;
    styles.paddingBottom = small;
  }
  if (verticalPadding) {
    styles.paddingTop = base;
    styles.paddingBottom = base;
  }
  if (largeVerticalPadding) {
    styles.paddingTop = large;
    styles.paddingBottom = large;
  }
  if (extraLargeVerticalPadding) {
    styles.paddingTop = extraLarge;
    styles.paddingBottom = extraLarge;
  }
  if (hugeVerticalPadding) {
    styles.paddingTop = huge;
    styles.paddingBottom = huge;
  }
  if (extraSmallPaddingV2) {
    styles.padding = spacingV2.extraSmall;
  }
  if (smallPaddingV2) {
    styles.padding = spacingV2.small;
  }
  if (basePaddingV2) {
    styles.padding = spacingV2.base;
  }
  if (largePaddingV2) {
    styles.padding = spacingV2.large;
  }
  if (extraLargePaddingV2) {
    styles.padding = spacingV2.extraLarge;
  }
  if (hugePaddingV2) {
    styles.padding = spacingV2.huge;
  }
  if (noHorizontalPaddingV2) {
    styles.paddingRight = spacingV2.none;
    styles.paddingLeft = spacingV2.none;
  }
  if (extraSmallHorizontalPaddingV2) {
    styles.paddingRight = spacingV2.extraSmall;
    styles.paddingLeft = spacingV2.extraSmall;
  }
  if (smallHorizontalPaddingV2) {
    styles.paddingRight = spacingV2.small;
    styles.paddingLeft = spacingV2.small;
  }
  if (horizontalPaddingV2) {
    styles.paddingRight = spacingV2.base;
    styles.paddingLeft = spacingV2.base;
  }
  if (largeHorizontalPaddingV2) {
    styles.paddingRight = spacingV2.large;
    styles.paddingLeft = spacingV2.large;
  }
  if (extraLargeHorizontalPaddingV2) {
    styles.paddingRight = spacingV2.extraLarge;
    styles.paddingLeft = spacingV2.extraLarge;
  }
  if (hugeHorizontalPaddingV2) {
    styles.paddingRight = spacingV2.huge;
    styles.paddingLeft = spacingV2.huge;
  }
  if (noVerticalPaddingV2) {
    styles.paddingTop = spacingV2.none;
    styles.paddingBottom = spacingV2.none;
  }
  if (extraSmallVerticalPaddingV2) {
    styles.paddingTop = spacingV2.extraSmall;
    styles.paddingBottom = spacingV2.extraSmall;
  }
  if (smallVerticalPaddingV2) {
    styles.paddingTop = spacingV2.small;
    styles.paddingBottom = spacingV2.small;
  }
  if (verticalPaddingV2) {
    styles.paddingTop = spacingV2.base;
    styles.paddingBottom = spacingV2.base;
  }
  if (largeVerticalPaddingV2) {
    styles.paddingTop = spacingV2.large;
    styles.paddingBottom = spacingV2.large;
  }
  if (extraLargeVerticalPaddingV2) {
    styles.paddingTop = spacingV2.extraLarge;
    styles.paddingBottom = spacingV2.extraLarge;
  }
  if (hugeVerticalPaddingV2) {
    styles.paddingTop = spacingV2.huge;
    styles.paddingBottom = spacingV2.huge;
  }
  if (paddingTop) {
    styles.paddingTop = paddingTop;
  }
  if (paddingRight) {
    styles.paddingRight = paddingRight;
  }
  if (paddingBottom) {
    styles.paddingBottom = paddingBottom;
  }
  if (paddingLeft) {
    styles.paddingLeft = paddingLeft;
  }
  if (marginTop) {
    styles.marginTop = marginTop;
  }
  if (marginRight) {
    styles.marginRight = marginRight;
  }
  if (marginBottom) {
    styles.marginBottom = marginBottom;
  }
  if (marginLeft) {
    styles.marginLeft = marginLeft;
  }
  return styles;
};

export const widths = {
  // we arrive at this number by using the maximum width the of the navbar at 220
  // and setting the browser min width to equal 768
  // 768 - 220 = 548
  MAIN_WINDOW_MIN_WIDTH: '548px',
};

export type mediaSize = 'extraSmall' | 'small' | 'medium' | 'large' | 'extraLarge';

export const mediaBreakpoints = {
  extraSmall: [0, 599],
  small: [600, 767],
  medium: [768, 991],
  large: [992, 1199],
  extraLarge: [1200, 999999],
} as Record<mediaSize, number[]>;

export const getMediaSize = (width: number | null): mediaSize => {
  const { small, medium, large, extraLarge } = mediaBreakpoints;
  if (!width) {
    return 'extraSmall';
  }
  switch (true) {
    case width >= extraLarge[0]:
      return 'extraLarge';
    case width >= large[0]:
      return 'large';
    case width >= medium[0]:
      return 'medium';
    case width >= small[0]:
      return 'small';
    default:
      return 'extraSmall';
  }
};

export enum EMediaSize {
  extraSmall = 1,
  small,
  medium,
  large,
  extraLarge,
}

export const isWithinMediaSizeOrBelow = (mediaSize: mediaSize, currentMediaSize: mediaSize) => {
  return EMediaSize[currentMediaSize] <= EMediaSize[mediaSize];
};

export interface IGetResponsiveStyleParams {
  responsive: boolean | undefined;
  mediaSize: mediaSize;
  extraLargeStyle?: CSS.Properties;
  largeStyle?: CSS.Properties;
  mediumStyle?: CSS.Properties;
  smallStyle?: CSS.Properties;
  extraSmallStyle?: CSS.Properties;
}

export const getResponsiveStyle = ({
  responsive,
  mediaSize,
  extraLargeStyle = {},
  largeStyle = {},
  mediumStyle = {},
  smallStyle = {},
  extraSmallStyle = {},
}: IGetResponsiveStyleParams) => {
  if (!responsive) {
    return {};
  }
  const size = EMediaSize[mediaSize];
  switch (size) {
    case EMediaSize.extraLarge:
      return { ...extraLargeStyle };
    case EMediaSize.large:
      return { ...extraLargeStyle, ...largeStyle };
    case EMediaSize.medium:
      return { ...extraLargeStyle, ...largeStyle, ...mediumStyle };
    case EMediaSize.small:
      return { ...extraLargeStyle, ...largeStyle, ...mediumStyle, ...smallStyle };
    case EMediaSize.extraSmall:
      return {
        ...extraLargeStyle,
        ...largeStyle,
        ...mediumStyle,
        ...smallStyle,
        ...extraSmallStyle,
      };
    default:
      return {};
  }
};

export const getFlexDirection = (
  flexDirectionBreakSize: mediaSize | undefined,
  mediaSize: mediaSize | undefined,
  defaultDirection: 'row' | 'column',
) => {
  const newDirection = defaultDirection === 'row' ? 'column' : 'row';
  if (!flexDirectionBreakSize || !mediaSize) {
    return defaultDirection;
  }
  if (EMediaSize[flexDirectionBreakSize] >= EMediaSize[mediaSize]) {
    return newDirection;
  }
  return defaultDirection;
};

export const getCustomTag = (defaultTag: string, customTag: string | undefined) => {
  return { '--tag': `${defaultTag}${customTag ? '-' + customTag : ''}` } as CSS.Properties;
};

export const fontColorsDefault = {
  muted: publicaColors.gray,
  base: publicaColors.veryDarkGray,
  highlighted: publicaColors.lightBlue,
};

export const highlightedDivStyle = {
  backgroundColor: publicaColors.white,
  border: `1px solid ${publicaColors.lightGray}`,
  borderRadius: '4px',
} as CSS.Properties;
